import { useEffect, useRef, useState, useCallback } from "react";
import RBush from "rbush";
import { debounce } from "lodash";

import getLatLng from "@components/GoogleMap/_lib/getLatLng";
import fitMapToMarkersBounds from "@components/GoogleMap/_lib/fitMapToMarkersBounds";

/**
 * Hook to manage visible markers using spatial indexing (rbush)
 * @param {Object[]} initialData - full marker dataset with .id, .lat, .lng
 * @param {google.maps.Map} map - Google Maps instance
 */
const useVisibleMarkers = ({ initialData, map }) => {
  const [visibleMarkers, setVisibleMarkers] = useState([]);
  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);

  const treeRef = useRef(null);
  const initialVisibleMarkersRef = useRef(null);
  const debouncedUpdateRef = useRef(null);
  const filtersAppliedRef = useRef(false);

  useEffect(() => {
    filtersAppliedRef.current = hasFiltersApplied;
  }, [hasFiltersApplied]);

  // Build RBush spatial index whenever data changes
  useEffect(() => {
    if (!initialData?.length) {
      return;
    }

    const tree = new RBush();
    const items = initialData.map((item) => {
      const { lat, lng } = getLatLng(item);
      return { minX: lng, minY: lat, maxX: lng, maxY: lat, item };
    });

    tree.load(items);
    treeRef.current = tree;
  }, [initialData]);

  // Query visible markers from bounds
  const queryMarkersInBounds = useCallback(() => {
    if (!treeRef.current) {
      return;
    }

    const bounds = map.getBounds();

    if (!bounds) {
      return;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return treeRef.current
      .search({
        minX: sw.lng(),
        minY: sw.lat(),
        maxX: ne.lng(),
        maxY: ne.lat(),
      })
      .map((entry) => entry.item);
  }, [map]);

  // Update visible markers state
  const updateVisibleMarkers = useCallback(() => {
    if (filtersAppliedRef.current) {
      return; // skip updating from bounds if filters are applied
    }

    const results = queryMarkersInBounds();

    if (results.length) {
      // Save initial markers
      if (!initialVisibleMarkersRef.current?.length) {
        initialVisibleMarkersRef.current = results;
      }

      setVisibleMarkers(results);
    }
  }, [queryMarkersInBounds]);

  // Attach debounced listener to map idle
  useEffect(() => {
    if (!map) return;

    debouncedUpdateRef.current = debounce(() => {
      if (!map.getBounds() || filtersAppliedRef.current) {
        return;
      }

      updateVisibleMarkers();
    }, 200);

    const listener = map.addListener("idle", debouncedUpdateRef.current);

    return () => {
      listener?.remove();
      debouncedUpdateRef.current?.cancel();
      filtersAppliedRef.current = false;
    };
  }, [map, updateVisibleMarkers]);

  // Geolocation update — re-query visible markers after map pans to user location
  const changeInitialVisibleMarkers = useCallback(() => {
    const results = queryMarkersInBounds();

    if (results?.length) {
      initialVisibleMarkersRef.current = results;

      setVisibleMarkers(results);
    }
  }, [queryMarkersInBounds]);

  const handleVisibleMarkersUpdate = (markers) => {
    debouncedUpdateRef.current?.cancel();

    if (markers?.length) {
      setHasFiltersApplied(true);
      fitMapToMarkersBounds({ markers, map });
      setVisibleMarkers(markers);
    } else {
      setHasFiltersApplied(false);
      fitMapToMarkersBounds({ markers: initialVisibleMarkersRef.current, map });
      setVisibleMarkers(initialVisibleMarkersRef.current);
    }
  };

  return {
    visibleMarkers,
    changeInitialVisibleMarkers,
    handleVisibleMarkersUpdate,
  };
};

export default useVisibleMarkers;
